import React from "react";
import { Autocomplete, Box, Button, InputLabel, OutlinedInput } from "@mui/material";
import FormSection from "../../Forms/FormSection/form-section";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import { DbUserGroupInfos } from "../../../Core/Api/Types/db-user-group-infos";
import { DbLabProviderInfos } from "../../../Core/Api/Types/db-lab-provider-infos";
import { DbUserGroupEntityInfos } from "../../../Core/Api/Types/db-user-group-entity-infos";
import { DbUserGroupRoleInfos } from "../../../Core/Api/Types/db-user-group-role-infos";
import { useTranslation } from "react-i18next";

interface ConfigGroupAssociationsProps {
  currencies: CurrencyTypes[] | null;
  groupInfos: DbUserGroupInfos | DbUserGroupRoleInfos | any;
  labProviders: DbLabProviderInfos[] | null;
  entities: DbUserGroupEntityInfos[] | null;
  titleIds: number[];
  context?: "Role" | "Group";
  onSetGroupInfos?: (value: any, context: "currency" | "labProviders" | "entity") => void;
  onModalOpen?: () => void;
}

const ConfigGroupAssociations: React.FC<ConfigGroupAssociationsProps> = (props) => {
  const { t } = useTranslation();
  const {
    currencies,
    groupInfos,
    labProviders,
    entities,
    titleIds,
    context = "Group",
    onSetGroupInfos,
    onModalOpen,
  } = props;

  return (
    <FormSection title={t("form:section.title.groupAssociations")} status="todo" sx={{ mb: 2 }}>
      {currencies && (
        <>
          <InputLabel sx={{ mb: 1 }}>{t("field:label.selectCurrency")}</InputLabel>
          <Autocomplete
            value={groupInfos.currency}
            id={"currency"}
            sx={{ width: "100%" }}
            onChange={(e, value) => {
              onSetGroupInfos && onSetGroupInfos(value, "currency");
            }}
            renderInput={(params) => (
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                required
                sx={{ width: "100%", mb: 2 }}
              />
            )}
            options={currencies}
            renderOption={(props, option) => (
              <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                {option}
              </Box>
            )}
          />
        </>
      )}

      {labProviders && context === "Group" && (
        <>
          <InputLabel sx={{ mb: 1 }}>{t("field:label.selectProviders")}</InputLabel>
          <Autocomplete
            multiple
            options={labProviders}
            value={labProviders.filter((x) => groupInfos.labProviderIds.includes(x.id))}
            popupIcon={<ExpandMoreRoundedIcon />}
            getOptionLabel={(option: any) => option.labName}
            renderOption={(props, option) => (
              <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                {option.labName}
              </Box>
            )}
            renderInput={(params) => (
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                required
                sx={{ width: "100%", mb: 2 }}
              />
            )}
            sx={{ width: "100%" }}
            id={"labProviders"}
            onChange={(e, value) => {
              onSetGroupInfos && onSetGroupInfos(value, "labProviders");
            }}
          />
        </>
      )}
      {entities && context === "Group" && (
        <>
          <InputLabel sx={{ mb: 1 }}>{t("field:label.selectEntity")}</InputLabel>
          <Autocomplete
            value={entities.find((x) => x.id === groupInfos.entityId) || null}
            id={"entity"}
            sx={{ width: "100%" }}
            onChange={(e, value) => {
              onSetGroupInfos && onSetGroupInfos(value, "entity");
            }}
            getOptionLabel={(option: any) => option.name || null}
            renderInput={(params) => (
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                required
                sx={{ width: "100%", mb: 3 }}
              />
            )}
            options={entities}
            renderOption={(props, option) => (
              <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                {option.name}
              </Box>
            )}
          />
        </>
      )}
      {groupInfos.$type === "VDM.AirLab.Sdk.Core.Database.Types.DbUserGroupInfos, VDM.AirLab.Sdk.Core" && (
        <Button variant="contained" color="primary" onClick={() => onModalOpen && onModalOpen()}>
          {t("button:filterTitleIdsSelected", { count: titleIds.length })}
        </Button>
      )}
    </FormSection>
  );
};

export default ConfigGroupAssociations;
